import type { ErrorLike } from '@apollo/client';
import { useCallback } from 'react';

import {
  type AtlasGqlTableView,
  AtlasGqlViewType,
  useGetBladeDefectPatternsLazyQuery,
  useGetBladeDefectPatternsQuery,
  useGetLegacyBladeDefectPatternsLazyQuery,
  useGetLegacyBladeDefectPatternsQuery,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { DEFECT_PATTERNS } from 'utils/release-toggles';

type DefectPatternViewsResult = {
  views: AtlasGqlTableView[];
  loading: boolean;
  error?: ErrorLike;
};

export type DefectPatternViewCustomFields = {
  bladeMake?: string;
  bladeModel?: string;
  [key: string]: unknown;
};

export const isDefectPatternViewType = (type?: AtlasGqlViewType | null): boolean =>
  type === AtlasGqlViewType.BladeDefectPattern || type === AtlasGqlViewType.BladeHotZone;

// A Turbine-matched Defect Pattern has no Blade make/model, so it names a representative
// Blade to scale diagrams against. Half a pair reads as absent, never mixed with filters.
export const getRepresentativeBlade = (
  view?: AtlasGqlTableView | null
): { make: string; model: string } | undefined => {
  const { bladeMake, bladeModel } =
    (view?.customFields as DefectPatternViewCustomFields | null) ?? {};
  return bladeMake && bladeModel ? { make: bladeMake, model: bladeModel } : undefined;
};

export const useDefectPatternViews = (): DefectPatternViewsResult => {
  const { hasReleaseToggle } = useAccountContext();
  const hasDefectPatterns = hasReleaseToggle(DEFECT_PATTERNS);

  const legacyDefectPatterns = useGetLegacyBladeDefectPatternsQuery({ skip: hasDefectPatterns });
  const defectPatterns = useGetBladeDefectPatternsQuery({ skip: !hasDefectPatterns });

  return hasDefectPatterns
    ? {
        views: defectPatterns.data?.bladeDefectPatternTableViews ?? [],
        loading: defectPatterns.loading,
        error: defectPatterns.error,
      }
    : {
        views: legacyDefectPatterns.data?.bladeHotZoneTableViews ?? [],
        loading: legacyDefectPatterns.loading,
        error: legacyDefectPatterns.error,
      };
};

export const useDefectPatternViewsLazy = (): (() => Promise<AtlasGqlTableView[]>) => {
  const { hasReleaseToggle } = useAccountContext();
  const hasDefectPatterns = hasReleaseToggle(DEFECT_PATTERNS);

  const [loadLegacyDefectPatterns] = useGetLegacyBladeDefectPatternsLazyQuery();
  const [loadDefectPatterns] = useGetBladeDefectPatternsLazyQuery();

  return useCallback(async () => {
    if (hasDefectPatterns) {
      const { data } = await loadDefectPatterns();
      return data?.bladeDefectPatternTableViews ?? [];
    }
    const { data } = await loadLegacyDefectPatterns();
    return data?.bladeHotZoneTableViews ?? [];
  }, [hasDefectPatterns, loadDefectPatterns, loadLegacyDefectPatterns]);
};
